import React, { useState } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from '../ui/progress';
import CompanyDataReview from '../CompanyDataReview';
import ComplianceGuidance from '../ComplianceGuidance';
import { analyzeDomain } from '../../services/openai';
import { questions } from './questions';

const NIS2Assessment = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState('input');
  const [companyData, setCompanyData] = useState(null);

  const handleDomainSubmit = async (e) => {
    e.preventDefault();
    setAnalyzing(true);
    
    try {
      const result = await analyzeDomain(answers.domain);
      if (result.error) {
        console.error('Error analyzing domain:', result.message);
      } else {
        console.log('Received company data:', result);
        setCompanyData(result);
        setStep('review');
      }
    } catch (error) {
      console.error('Error analyzing domain:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCompanyDataComplete = (data) => {
    setCompanyData(data);
    setStep('guidance');
  };

  const renderQuestion = () => {
    const currentQuestion = questions[currentStep];
    
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{currentQuestion.text}</h2>
        {currentQuestion.description && (
          <p className="text-gray-600 text-sm whitespace-pre-line">
            {currentQuestion.description}
          </p>
        )}
        
        <div className="space-y-2">
          {currentQuestion.type === 'text' ? (
            <Input
              type="text"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) =>
                setAnswers((prev) => ({
                  ...prev,
                  [currentQuestion.id]: e.target.value,
                }))
              }
              placeholder={`Enter ${currentQuestion.id}`}
              className="w-full"
            />
          ) : (
            currentQuestion.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  id={option.value}
                  name={currentQuestion.id}
                  value={option.value}
                  checked={answers[currentQuestion.id] === option.value}
                  onChange={(e) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [currentQuestion.id]: e.target.value,
                    }))
                  }
                />
                <label htmlFor={option.value}>{option.label}</label>
              </div>
            ))
          )}
        </div>

        {analyzing ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="flex justify-between mt-4">
            {currentStep > 0 && (
              <Button
                onClick={() => setCurrentStep((prev) => prev - 1)}
                variant="outline"
              >
                Previous
              </Button>
            )}
            {currentStep === 0 ? (
              <Button
                onClick={handleDomainSubmit}
                disabled={!answers.domain || analyzing}
              >
                Analyze Domain
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep((prev) => prev + 1)}
                disabled={!answers[questions[currentStep].id]}
              >
                Next
              </Button>
            )}
          </div>
        )}

        <Progress
          value={(currentStep / (questions.length - 1)) * 100}
          className="mt-4"
        />
      </div>
    );
  };

  const renderCompanyDataReview = () => {
    return (
      <CompanyDataReview
        initialData={companyData}
        onComplete={handleCompanyDataComplete}
        onUpdate={(data) => setCompanyData(data)}
      />
    );
  };

  const restart = () => {
    setCurrentStep(0);
    setAnswers({});
    setAnalyzing(false);
    setStep('input');
    setCompanyData(null);
  };

  return (
    <div>
      {step === 'input' && (
        <form onSubmit={handleDomainSubmit} className="max-w-md mx-auto">
          {renderQuestion()}
        </form>
      )}

      {step === 'review' && companyData && (
        <div className="relative z-0">
          {renderCompanyDataReview()}
        </div>
      )}

      {step === 'guidance' && companyData && (
        <div className="space-y-6">
          <ComplianceGuidance companyData={companyData} />
          
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => setStep('review')}
            >
              Edit Company Data
            </Button>
            <Button
              variant="outline"
              onClick={restart}
            >
              Start Over
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NIS2Assessment;